import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { toast } from "sonner";
import { Plus, Plane, Ship, Edit2, Power, RotateCcw, Euro } from "lucide-react";

const MODES = ["luchtvracht", "zeevracht"];
const MODE_LABELS = { luchtvracht: "Luchtvracht", zeevracht: "Zeevracht" };
const MODE_ICONS = { luchtvracht: Plane, zeevracht: Ship };
const UNITS = ["per_kg", "per_m3", "per_collo", "vast_bedrag"];
const UNIT_LABELS = {
  per_kg: "per kg",
  per_m3: "per m³",
  per_collo: "per collo",
  vast_bedrag: "vast bedrag",
};
// Sprint 3b — 6 Mrt 2026: XCG vervangt ANG voor nieuwe klant-tarieven.
// ANG-symbool blijft in de map voor read-only rendering van bestaande tarieven.
import { SUPPORTED_CURRENCIES } from "../../../lib/currencies";
const CURRENCIES = SUPPORTED_CURRENCIES;
const CURRENCY_SYMBOL = {
  EUR: "€", XCG: "ƒ", USD: "$", AWG: "ƒ", SRD: "SRD",
  JMD: "J$", COP: "$", DOP: "RD$", GYD: "G$", PAB: "B/.",
  GBP: "£", CHF: "CHF", CAD: "C$", AUD: "A$",
  // Legacy
  ANG: "ƒ",
};

function formatRate(rate) {
  const sym = CURRENCY_SYMBOL[rate.currency] || rate.currency;
  const amt = Number(rate.rate_amount || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return `${sym} ${amt} ${UNIT_LABELS[rate.unit] || rate.unit}`;
}

function emptyForm(mode = "luchtvracht") {
  return {
    mode,
    destination: "",
    unit: mode === "zeevracht" ? "per_m3" : "per_kg",
    rate_amount: "",
    currency: "EUR",
    min_weight_kg: "",
    max_weight_kg: "",
    valid_from: "",
    valid_until: "",
    notes: "",
  };
}

function RateForm({ form, setForm, disabled }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tariff-form">
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Type vracht *</Label>
        <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })} disabled={disabled}>
          <SelectTrigger className="rounded-none mt-1" data-testid="tariff-field-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MODES.map((m) => <SelectItem key={m} value={m}>{MODE_LABELS[m]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Bestemming</Label>
        <Input
          value={form.destination}
          onChange={(e) => setForm({ ...form, destination: e.target.value })}
          placeholder="Bv. Curaçao, Aruba, Bonaire"
          disabled={disabled}
          className="rounded-none mt-1"
          data-testid="tariff-field-destination"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Eenheid *</Label>
        <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })} disabled={disabled}>
          <SelectTrigger className="rounded-none mt-1" data-testid="tariff-field-unit"><SelectValue /></SelectTrigger>
          <SelectContent>
            {UNITS.map((u) => <SelectItem key={u} value={u}>{UNIT_LABELS[u]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs uppercase tracking-wide font-bold">Tarief *</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.rate_amount}
            onChange={(e) => setForm({ ...form, rate_amount: e.target.value })}
            placeholder="0,00"
            disabled={disabled}
            className="rounded-none mt-1"
            data-testid="tariff-field-rate"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide font-bold">Valuta</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })} disabled={disabled}>
            <SelectTrigger className="rounded-none mt-1" data-testid="tariff-field-currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Min. gewicht (kg)</Label>
        <Input
          type="number"
          step="0.1"
          min="0"
          value={form.min_weight_kg}
          onChange={(e) => setForm({ ...form, min_weight_kg: e.target.value })}
          disabled={disabled}
          className="rounded-none mt-1"
          data-testid="tariff-field-min-weight"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Max. gewicht (kg)</Label>
        <Input
          type="number"
          step="0.1"
          min="0"
          value={form.max_weight_kg}
          onChange={(e) => setForm({ ...form, max_weight_kg: e.target.value })}
          disabled={disabled}
          className="rounded-none mt-1"
          data-testid="tariff-field-max-weight"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Geldig vanaf</Label>
        <Input
          type="date"
          value={form.valid_from}
          onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
          disabled={disabled}
          className="rounded-none mt-1"
          data-testid="tariff-field-valid-from"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide font-bold">Geldig t/m</Label>
        <Input
          type="date"
          value={form.valid_until}
          onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
          disabled={disabled}
          className="rounded-none mt-1"
          data-testid="tariff-field-valid-until"
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wide font-bold">Interne notitie</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Bv. speciale klantafspraak, geldigheidsperiode, etc."
          rows={2}
          disabled={disabled}
          className="rounded-none mt-1"
          data-testid="tariff-field-notes"
        />
      </div>
    </div>
  );
}

function RateRow({ rate, canManage, onEdit, onDeactivate, onReactivate }) {
  const inactive = !rate.active;
  return (
    <tr
      className={`border-b border-gray-100 ${inactive ? "bg-gray-50 opacity-60" : "hover:bg-gray-50"}`}
      data-testid={`tariff-row-${rate.id}`}
    >
      <td className="p-3 text-sm font-medium text-[#0A192F]" data-testid={`tariff-row-${rate.id}-destination`}>
        {rate.destination || <span className="text-gray-400 italic">— alle —</span>}
      </td>
      <td className="p-3 text-sm" data-testid={`tariff-row-${rate.id}-unit`}>{UNIT_LABELS[rate.unit] || rate.unit}</td>
      <td className="p-3 text-sm font-mono" data-testid={`tariff-row-${rate.id}-amount`}>
        {formatRate(rate)}
      </td>
      <td className="p-3 text-xs text-gray-500" data-testid={`tariff-row-${rate.id}-weight-range`}>
        {rate.min_weight_kg != null || rate.max_weight_kg != null
          ? `${rate.min_weight_kg ?? "—"} – ${rate.max_weight_kg ?? "—"} kg`
          : "—"}
      </td>
      <td className="p-3 text-xs text-gray-500" data-testid={`tariff-row-${rate.id}-validity`}>
        {rate.valid_from || rate.valid_until
          ? `${rate.valid_from || "—"} t/m ${rate.valid_until || "—"}`
          : "Onbeperkt"}
      </td>
      <td className="p-3" data-testid={`tariff-row-${rate.id}-status`}>
        {inactive ? (
          <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold bg-gray-200 text-gray-700">Inactief</span>
        ) : (
          <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold bg-green-100 text-green-700">Actief</span>
        )}
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        {canManage && !inactive && (
          <>
            <button
              onClick={() => onEdit(rate)}
              className="p-1.5 text-gray-500 hover:text-[#0055FF] inline-block"
              title="Bewerken"
              data-testid={`tariff-row-${rate.id}-edit`}
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDeactivate(rate)}
              className="p-1.5 text-gray-500 hover:text-red-600 inline-block ml-1"
              title="Deactiveren"
              data-testid={`tariff-row-${rate.id}-deactivate`}
            >
              <Power className="w-4 h-4" />
            </button>
          </>
        )}
        {canManage && inactive && (
          <button
            onClick={() => onReactivate(rate)}
            className="p-1.5 text-gray-500 hover:text-green-600"
            title="Heractiveren"
            data-testid={`tariff-row-${rate.id}-reactivate`}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

function ModeSection({ mode, rates, canManage, onAdd, onEdit, onDeactivate, onReactivate }) {
  const Icon = MODE_ICONS[mode];
  const modeRates = rates.filter((r) => r.mode === mode);
  return (
    <div className="bg-white border border-gray-200" data-testid={`tariff-section-${mode}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-[#F8F9FA]">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#0055FF]" />
          <h3 className="font-chivo font-black text-sm uppercase tracking-wider text-[#0A192F]">
            {MODE_LABELS[mode]}
          </h3>
          <span className="text-xs text-gray-500">({modeRates.length} {modeRates.length === 1 ? "tarief" : "tarieven"})</span>
        </div>
        {canManage && (
          <Button
            onClick={() => onAdd(mode)}
            size="sm"
            className="rounded-none bg-[#0A192F] hover:bg-[#0055FF] text-white text-xs uppercase font-chivo font-bold"
            data-testid={`tariff-add-${mode}`}
          >
            <Plus className="w-3 h-3 mr-1" /> Tarief toevoegen
          </Button>
        )}
      </div>
      {modeRates.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500" data-testid={`tariff-empty-${mode}`}>
          Geen {MODE_LABELS[mode].toLowerCase()}-tarieven vastgelegd voor deze klant.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-gray-100">
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="p-3 font-chivo font-bold">Bestemming</th>
              <th className="p-3 font-chivo font-bold">Eenheid</th>
              <th className="p-3 font-chivo font-bold">Tarief</th>
              <th className="p-3 font-chivo font-bold">Gewichtsrange</th>
              <th className="p-3 font-chivo font-bold">Geldigheid</th>
              <th className="p-3 font-chivo font-bold">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {modeRates.map((rate) => (
              <RateRow
                key={rate.id}
                rate={rate}
                canManage={canManage}
                onEdit={onEdit}
                onDeactivate={onDeactivate}
                onReactivate={onReactivate}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function KlantTarievenTab({ customerId }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("customers:edit_full");
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // rate object or null for new
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = includeInactive ? { include_inactive: "true" } : {};
      const r = await adminApi.get(`/admin/customers/${customerId}/rates`, { params });
      setRates(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [customerId, includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const onAdd = (mode) => {
    setEditing(null);
    setForm(emptyForm(mode));
    setDialogOpen(true);
  };

  const onEdit = (rate) => {
    setEditing(rate);
    setForm({
      mode: rate.mode || "luchtvracht",
      destination: rate.destination || "",
      unit: rate.unit || "per_kg",
      rate_amount: rate.rate_amount ?? "",
      currency: rate.currency || "EUR",
      min_weight_kg: rate.min_weight_kg ?? "",
      max_weight_kg: rate.max_weight_kg ?? "",
      valid_from: rate.valid_from || "",
      valid_until: rate.valid_until || "",
      notes: rate.notes || "",
    });
    setDialogOpen(true);
  };

  const onSave = async () => {
    if (!form.rate_amount && form.rate_amount !== 0) {
      toast.error("Tarief is verplicht.");
      return;
    }
    const payload = {
      mode: form.mode,
      destination: form.destination || null,
      unit: form.unit,
      rate_amount: parseFloat(form.rate_amount),
      currency: form.currency,
      min_weight_kg: form.min_weight_kg === "" ? null : parseFloat(form.min_weight_kg),
      max_weight_kg: form.max_weight_kg === "" ? null : parseFloat(form.max_weight_kg),
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      notes: form.notes || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await adminApi.patch(`/admin/customer-rates/${editing.id}`, payload);
        toast.success("Tarief bijgewerkt");
      } else {
        await adminApi.post(`/admin/customers/${customerId}/rates`, payload);
        toast.success("Tarief toegevoegd");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async (rate) => {
    if (!window.confirm(`Weet je zeker dat je dit tarief wilt deactiveren?\n\n${MODE_LABELS[rate.mode]} · ${rate.destination || "—"} · ${formatRate(rate)}`)) {
      return;
    }
    try {
      await adminApi.delete(`/admin/customer-rates/${rate.id}`);
      toast.success("Tarief gedeactiveerd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onReactivate = async (rate) => {
    try {
      await adminApi.post(`/admin/customer-rates/${rate.id}/reactivate`);
      toast.success("Tarief heractiveerd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="bg-white border border-gray-200 p-6 text-sm text-gray-500">Tarieven laden…</div>;
  }

  return (
    <div className="space-y-3" data-testid="klant-tarieven-content">
      <div className="bg-blue-50 border border-blue-200 p-3 flex items-start gap-3 text-sm">
        <Euro className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-chivo font-bold text-[#0A192F]">Klanttarieven</div>
          <div className="text-xs text-gray-600 mt-0.5">
            Specifieke lucht- en zeevrachttarieven voor deze klant. Niet zichtbaar voor de klant; alleen intern bij offertes en facturen.
            {!canManage && " Bekijken alleen — voor wijzigen heb je rechten als Owner, Admin of Manager nodig."}
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap" data-testid="tariff-toggle-inactive">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded-none"
          />
          Toon inactieve
        </label>
      </div>

      <ModeSection
        mode="luchtvracht"
        rates={rates}
        canManage={canManage}
        onAdd={onAdd}
        onEdit={onEdit}
        onDeactivate={onDeactivate}
        onReactivate={onReactivate}
      />
      <ModeSection
        mode="zeevracht"
        rates={rates}
        canManage={canManage}
        onAdd={onAdd}
        onEdit={onEdit}
        onDeactivate={onDeactivate}
        onReactivate={onReactivate}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="tariff-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-xl text-[#0A192F]">
              {editing ? "Tarief bewerken" : "Nieuw tarief toevoegen"}
            </DialogTitle>
          </DialogHeader>
          <RateForm form={form} setForm={setForm} disabled={saving} />
          <DialogFooter className="gap-2">
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outline"
              className="rounded-none"
              data-testid="tariff-dialog-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              className="rounded-none bg-[#0A192F] hover:bg-[#0055FF] text-white font-chivo font-bold uppercase"
              data-testid="tariff-dialog-save"
            >
              {saving ? "Opslaan…" : editing ? "Wijzigingen opslaan" : "Tarief toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
